import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {creatorActions} from "../../../store/slice/gateCreatorSlice";
import {useMutation, useQuery} from "react-query";
import {authorizedGet, authorizedPutWithId} from "../CreatorFunctions";
import {Loader} from "@mantine/core";
import {motion} from "framer-motion";
import useTranslations from "../../../hooks/useTranslations";

const getGateValueForRecommendation = (type, creatorItem, gateSurface) => {
    switch (type) {
        case 'surface':
            return gateSurface;
        case 'width':
            return creatorItem?.width?.value;
        case 'height':
            return creatorItem?.height?.value;
        case 'depth':
            return creatorItem?.depth;
        default:
            return null;
    }
};

const isRecommendedDrive = (drive, creatorItem, gateSurface) => {
    if (!drive?.isRecommended || drive?.recommendationValue == null || !drive?.recommendationType) {
        return false;
    }
    const gateValue = getGateValueForRecommendation(drive.recommendationType, creatorItem, gateSurface);
    if (gateValue == null) {
        return false;
    }
    return gateValue <= drive.recommendationValue;
};

const pickRecommendedDrive = (drives, creatorItem, gateSurface) => {
    const candidates = drives.filter(drive => isRecommendedDrive(drive, creatorItem, gateSurface));
    if (!candidates.length) {
        return null;
    }
    return [...candidates].sort((a, b) => {
        if (a.recommendationValue !== b.recommendationValue) {
            return a.recommendationValue - b.recommendationValue;
        }
        return (a.driveGroupOrder ?? 100) - (b.driveGroupOrder ?? 100);
    })[0];
};

const Drive = () => {
    const dispatch = useDispatch();
    const creatorItem = useSelector(state => state.gateCreator);
    const [chosenDrive, setChosenDrive] = useState(creatorItem?.drive?.id);
    const autoSelectedRef = useRef(false);
    const creatorItemRef = useRef(creatorItem);
    creatorItemRef.current = creatorItem;

    const {isLoading} = useQuery(
        ["gateType", creatorItem],
        () => authorizedGet(`/api/gate_types${creatorItem?.gateType?.id ? "/" + creatorItem?.gateType?.id : ""}`),
        {keepPreviousData: true}
    );
    const {data: chosenGateTypeAllDrives} = useQuery(
        "chosenGateTypeDrives",
        () => authorizedGet(`/api/gate_types/${creatorItem?.gateType?.id}/drive_groups`),
        {placeholderData: []}
    );
    const driveGroups = chosenGateTypeAllDrives.driveGroups || [];

    const updateGateMutation = useMutation(authorizedPutWithId, {
        onSuccess: (d) => {
            dispatch(creatorActions.updateCreator(d))
        }
    })
    const updateGate = updateGateMutation.mutate;
    const [filteredDriveGroups, setFilteredDriveGroups] = useState([])
    const gateSurface = (creatorItem.height.value * creatorItem.width.value) / 1000000;

    useEffect(() => {
        const filteredDrives = [];
        for (const driveGroup of driveGroups) {
            for (const drive of driveGroup.drives) {
                for (const leading of drive.leadings) {
                    if (leading?.id !== creatorItem?.leading?.id) continue
                    if (!(drive?.minHeight <= creatorItem?.lintel?.value && drive?.maxHeight >= creatorItem?.lintel?.value)) continue
                    if (!(drive?.minTotalHeight <= creatorItem?.height?.value && drive?.maxTotalHeight >= creatorItem?.height?.value)) continue
                    if (!(drive?.depth <= creatorItem?.depth && drive?.leftArea <= creatorItem?.leftArea && drive?.rightArea <= creatorItem?.rightArea && drive?.maximalSurface >= gateSurface)) continue

                    filteredDrives.push({
                        ...drive,
                        driveGroupOrder: driveGroup.driveGroupOrder,
                        isRecommended: driveGroup.isRecommended,
                        recommendationType: driveGroup.recommendationType,
                        recommendationValue: driveGroup.recommendationValue,
                        driveGroup,
                    })
                }
            }
        }
        setFilteredDriveGroups(filteredDrives)
    }, [driveGroups, gateSurface, creatorItem])

    const recommendedDrive = useMemo(
        () => pickRecommendedDrive(filteredDriveGroups, creatorItem, gateSurface),
        [filteredDriveGroups, creatorItem, gateSurface]
    );

    const selectDrive = useCallback((drive) => {
        const current = creatorItemRef.current;
        setChosenDrive(drive.id)
        dispatch(creatorActions.updateCreator({
            ...current,
            drive: drive
        }))
        updateGate({
            url: '/api/gates',
            data: {
                drive: drive?.id,
                offer: current?.isEdit ? current.offerId : null,
            },
            id: current.id
        })
    }, [dispatch, updateGate]);

    useEffect(() => {
        if (autoSelectedRef.current) return;
        if (creatorItem?.drive?.id) {
            autoSelectedRef.current = true;
            return;
        }
        if (!filteredDriveGroups.length) return;

        const driveToSelect = recommendedDrive
            || [...filteredDriveGroups].sort((a, b) => (a.driveGroupOrder ?? 100) - (b.driveGroupOrder ?? 100))[0];

        if (!driveToSelect) return;

        autoSelectedRef.current = true;
        selectDrive(driveToSelect);
    }, [filteredDriveGroups, recommendedDrive, creatorItem?.drive?.id, selectDrive]);

    const {
        chooseButton, chosenButton,
        driveNoDrives,
        driveRecommended,
        language,
    } = useTranslations();

    return (
        <div className="flex">
            <div className="content-items content-items__medium content-items__small p-24">
                {isLoading && <div style={{width: "100%", textAlign: "center", padding: 20}}><Loader/></div>}
                {!isLoading && filteredDriveGroups.length === 0 &&
                    <p>{driveNoDrives}</p>}
                {!isLoading && filteredDriveGroups?.length > 0 && <div className="items-container gap-24 show">
                    {filteredDriveGroups?.sort((a, b) => a.driveGroupOrder - b.driveGroupOrder).map(drive => {
                        const isRecommendedCard = recommendedDrive?.id === drive.id;
                        return (
                            <motion.div
                                initial={{
                                    opacity: 0
                                }}
                                animate={{
                                    opacity: 1
                                }}
                                style={{
                                    backgroundImage: drive?.image?.contentUrl ? `url(${process.env.REACT_APP_API_UPLOADS + drive?.image?.contentUrl})` : null,
                                    backgroundSize: "cover",
                                    backgroundBlendMode: "screen",
                                    flex: 1
                                }}
                                key={drive.id}
                                className={`content-item content-item1 content-item__drive${isRecommendedCard ? " content-item__drive--recommended" : ""}`}>
                                {isRecommendedCard && (
                                    <span className="content-item__recommended-badge">
                                        {driveRecommended || "Zalecane"}
                                    </span>
                                )}
                                <p className="content-item__title">{language === 'pl' ? drive.name : drive?.translations ? drive?.translations[language].name : '-'}</p>
                                <button
                                    onClick={() => selectDrive(drive)}
                                    className={`content-item__link ${chosenDrive === drive.id ? "chosen" : ""}`}
                                >{chosenDrive !== drive.id ? chooseButton : chosenButton}</button>
                            </motion.div>
                        )
                    })}
                </div>}
            </div>
        </div>
    );
};

export default Drive;
